import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Lock, LockOpen, Loader2, AlertCircle, KeyRound, Info,
  ShieldCheck, Heart, Download, FileText, BookOpen,
  Building2, EyeOff, BadgeCheck,
} from 'lucide-react'
import { useLibroPublicInfo, useValidarCodigo } from '../../hooks/useQrCodigo'

export function ValidarCodigoPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const [codigo, setCodigo] = useState('')
  const [error, setError] = useState('')
  const [isHovering, setIsHovering] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const validarMutation = useValidarCodigo()
  const { data: libro } = useLibroPublicInfo(uuid)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    setCodigo(value)
    if (value.length >= 3) setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!uuid || !codigo.trim()) return
    setError('')

    try {
      const result = await validarMutation.mutateAsync({ uuid, codigo: codigo.trim().toUpperCase() })

      if (!result.valido) {
        if (result.mensaje === 'codigo_usado') {
          navigate(`/qr/${uuid}/usado`, { state: { codigoUsado: codigo.trim().toUpperCase() } })
        } else {
          setError(result.mensaje || 'Código no válido. Verifica e intenta nuevamente.')
        }
        return
      }

      if (result.mensaje === 'canje_directo') {
        navigate(`/qr/descarga/${encodeURIComponent(result.token!)}`)
      } else {
        navigate(`/qr/${uuid}/formulario`, { state: { token: result.token } })
      }
    } catch {
      setError('Error al validar el código. Intente nuevamente.')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-4 px-3 sm:py-6 sm:px-4 lg:px-8"
         style={{ backgroundColor: '#eff6ff' }}>
      <div className="max-w-3xl w-full">

        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl overflow-hidden">

          {/* Book Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                {/* Book Image */}
                <div className="flex-shrink-0">
                  {libro?.imagen_portada_url ? (
                    <img
                      src={libro.imagen_portada_url}
                      alt={libro.nombre}
                      className="w-32 h-44 sm:w-40 sm:h-52 lg:w-44 lg:h-60 object-cover rounded-lg shadow-xl border-2 border-white/20"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                        ;(e.target as HTMLImageElement).nextElementSibling?.setAttribute('style', 'display:flex')
                      }}
                    />
                  ) : null}
                  <div
                    className="w-32 h-44 sm:w-40 sm:h-52 lg:w-44 lg:h-60 bg-white/10 rounded-lg items-center justify-center border-2 border-white/20"
                    style={{ display: libro?.imagen_portada_url ? 'none' : 'flex' }}
                  >
                    <BookOpen className="h-10 w-10 opacity-60" />
                  </div>
                </div>

                {/* Book Info */}
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-3">
                    <span className="inline-flex items-center bg-green-500/20 text-green-100 px-3 py-1 rounded-full text-xs font-semibold border border-green-400/30">
                      <Heart className="h-3 w-3 mr-1" />
                      Área Médica Profesional
                    </span>
                    <span className="inline-flex items-center bg-white/15 text-white px-3 py-1 rounded-full text-xs font-semibold border border-white/20">
                      <ShieldCheck className="h-3 w-3 mr-1" />
                      Acceso Seguro
                    </span>
                  </div>

                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                    <h2 className="text-xl sm:text-2xl font-bold">
                      {libro?.nombre || 'Cargando...'}
                    </h2>
                    {libro?.descripcion && (
                      <button
                        onClick={() => setShowInfo(!showInfo)}
                        className="text-white/70 hover:text-white transition-colors shrink-0"
                      >
                        <Info className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                  {showInfo && libro?.descripcion && (
                    <p className="text-sm opacity-85 animate-in fade-in slide-in-from-top-1 duration-200 mb-2">
                      {libro.descripcion}
                    </p>
                  )}

                  {libro?.autor && (
                    <p className="text-sm opacity-90 mb-2 flex items-center justify-center sm:justify-start">
                      <FileText className="h-3.5 w-3.5 mr-1" />
                      {libro.autor}
                    </p>
                  )}

                  {(libro?.editorial || libro?.paginas) && (
                    <div className="flex flex-wrap justify-center sm:justify-start gap-4 mt-3 text-xs opacity-80">
                      {libro.editorial && (
                        <div className="flex items-center">
                          <Building2 className="h-3 w-3 mr-1" />
                          {libro.editorial}
                        </div>
                      )}
                      {libro.paginas && (
                        <div className="flex items-center">
                          <FileText className="h-3 w-3 mr-1" />
                          {libro.paginas} páginas
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Code Input Section */}
              <div className="bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-200">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 flex items-center justify-center">
                  <KeyRound className="h-4 w-4 text-blue-600 mr-2" />
                  Ingresa tu código de acceso
                </h3>

                <div className="bg-white rounded-lg p-2 sm:p-3 border border-gray-200">
                  <input
                    type="text"
                    value={codigo}
                    onChange={handleInputChange}
                    placeholder="EJ: MED2024X"
                    maxLength={12}
                    autoFocus
                    autoComplete="off"
                    className="w-full px-3 sm:px-6 py-3 text-center text-xl sm:text-2xl font-bold tracking-wider uppercase border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
                    style={error ? { borderColor: '#ef4444', boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)' } : undefined}
                  />

                  {/* Error Message */}
                  {error && (
                    <div className="mt-2 text-red-600 text-xs sm:text-sm text-center p-2 bg-red-50 rounded-lg border border-red-200 flex items-center justify-center animate-in fade-in slide-in-from-top-1 duration-200">
                      <AlertCircle className="h-3.5 w-3.5 mr-1 shrink-0" />
                      {error}
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-500 text-center mt-2">
                  El código es único y solo puede utilizarse una vez
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!codigo.trim() || validarMutation.isPending}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                className="w-full flex justify-center items-center px-4 sm:px-6 py-3 border border-transparent rounded-lg text-sm sm:text-base font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg"
              >
                {validarMutation.isPending ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <span className="mr-2">
                    {isHovering ? (
                      <LockOpen className="h-4 w-4 inline-block transition-all" style={{ transform: 'scale(1.1)' }} />
                    ) : (
                      <Lock className="h-4 w-4 inline-block transition-all" />
                    )}
                  </span>
                )}
                <span>{validarMutation.isPending ? 'Validando...' : 'Acceder al Contenido Médico'}</span>
              </button>

              {/* Additional Info */}
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 border border-gray-200">
                <div className="text-center text-xs sm:text-sm text-gray-600">
                  <div className="flex items-center justify-center gap-3 sm:gap-6 flex-wrap">
                    <div className="flex items-center">
                      {libro?.requiere_formulario ? (
                        <>
                          <FileText className="h-3.5 w-3.5 mr-1 text-gray-400" />
                          <span>Requiere completar formulario</span>
                        </>
                      ) : (
                        <>
                          <Download className="h-3.5 w-3.5 mr-1 text-green-600" />
                          <span className="text-green-700 font-semibold">Descarga directa</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center">
                      <BadgeCheck className="h-3.5 w-3.5 mr-1 text-gray-400" />
                      <span className="hidden sm:inline">Términos y condiciones aplican</span>
                      <span className="sm:hidden">T&C aplican</span>
                    </div>
                  </div>
                </div>
              </div>
            </form>

            {/* Instructions */}
            <div className="mt-4 bg-blue-50 rounded-lg p-3 border border-blue-200">
              <h4 className="text-xs sm:text-sm font-semibold text-blue-900 mb-1 flex items-center">
                <Info className="h-3.5 w-3.5 mr-1" />
                Instrucciones de Acceso
              </h4>
              <p className="text-xs sm:text-sm text-blue-700">
                {libro?.requiere_formulario
                  ? 'Introduce el código de acceso que recibiste. Después de validar el código, deberás completar un formulario antes de poder descargar el material médico.'
                  : 'Introduce el código de acceso que recibiste. Una vez validado, podrás descargar el material médico inmediatamente sin necesidad de completar formularios adicionales.'
                }
                {' '}Cada código es único y solo puede utilizarse una vez por motivos de seguridad.
              </p>
            </div>

            {/* Security Features */}
            <div className="mt-3 bg-green-50 rounded-lg p-3 border border-green-200">
              <h4 className="text-xs sm:text-sm font-semibold text-green-900 mb-2 flex items-center">
                <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                Seguridad y Privacidad
              </h4>
              <div className="grid grid-cols-2 gap-1 sm:gap-2 text-xs text-green-700">
                <div className="flex items-center">
                  <Lock className="h-3 w-3 mr-1 text-green-600" />
                  <span className="hidden sm:inline">Acceso encriptado</span>
                  <span className="sm:hidden">Encriptado</span>
                </div>
                <div className="flex items-center">
                  <BadgeCheck className="h-3 w-3 mr-1 text-green-600" />
                  <span className="hidden sm:inline">Contenido verificado</span>
                  <span className="sm:hidden">Verificado</span>
                </div>
                <div className="flex items-center">
                  <EyeOff className="h-3 w-3 mr-1 text-green-600" />
                  <span className="hidden sm:inline">Datos protegidos</span>
                  <span className="sm:hidden">Protegido</span>
                </div>
                <div className="flex items-center">
                  <Heart className="h-3 w-3 mr-1 text-green-600" />
                  <span className="hidden sm:inline">Estándar médico</span>
                  <span className="sm:hidden">Médico</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 sm:mt-8">
          <p className="text-sm text-gray-500 flex items-center justify-center flex-wrap">
            <span className="flex items-center mr-3">
              <Heart className="h-3.5 w-3.5 mr-1 text-green-600" />
              Plataforma Médica Profesional
            </span>
            <span className="flex items-center">
              <ShieldCheck className="h-3.5 w-3.5 mr-1 text-blue-600" />
              Powered by <strong className="ml-1">Tonal Strategies</strong>
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}
